import { Data, FormErrors } from '../../../../../molecules/form/Form';
import styles from './EmploymentContent.module.css'
import Text from '../../../../../atoms/typography/Text';
import { ColorEnum, FontWeightEnum } from '../../../../../enums';
import Label from '../../../../../atoms/label/Label';
import { FileUpload } from '../../../../../organisms/file-upload';
import { CheckboxInput } from '../../../../../molecules/checkbox-input';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContractTypeEnum from '../../../../../enums/screening/ContractTypeEnum';
import { FindIncomeSourceEmploymentResponse } from '../../../../../../api/screening/income-verification/find-income-source-documents/findIncomeSourceEmployment';
import { StoredFileMetadata } from '../../../../../../api/shared/types';
import { FileMetaData } from '../../../../../organisms/file-upload/FileUpload';
import moment from 'moment';
import IncomeVerificationIncomeSourceEmploymentDocumentEnum from '../../../../../enums/screening/IncomeVerificationIncomeSourceEmploymentDocumentEnum';
import IncomeFrequencyEnum from '../../../../../enums/screening/IncomeFrequencyEnum';
import { AnimationWrapperEaseInOut } from '../../../../../atoms/animationWrapperEaseInOut';

interface EmploymentContentProps {
    storedData: FindIncomeSourceEmploymentResponse;
    formData: Data;
    formErrors: FormErrors;
    onChange: (data) => void;
    onDocumentDelete?: (key: string, name: string) => void;
}

const EmploymentContent = ({
    storedData,
    formData,
    formErrors,
    onChange,
    onDocumentDelete,
}: EmploymentContentProps) => {
    const { t } = useTranslation();

    const [allFilesRequired, setAllFilesRequired] = useState(true);
    const [ allRequiredFilesUploaded, setAllRequiredFilesUploaded ] = useState(false);

    const contractType = storedData.incomeSource.contractType as ContractTypeEnum;

    useEffect(() => {
        onChange({
            employmentContract: formData['employmentContract']?.value as FileList,
            employerStatement: formData['employerStatement']?.value as FileList,
            payslipOne: formData['payslipOne']?.value as FileList,
            payslipTwo: formData['payslipTwo']?.value as FileList,
            payslipThree: formData['payslipThree']?.value as FileList
        });

        let allValid = true;

        for (const field in formData) {
            if (formData[field].isValid !== true) {
                allValid = false;
                break;
            }
        }

        setAllRequiredFilesUploaded(allValid);
    }, [formData]);

    const handleFileUploadDelete = (key: string, name: string) => {
        onDocumentDelete?.(key, name);
    }

    const isEmployerStatementRequired = (): boolean => {
        return (contractType !== ContractTypeEnum.ZeroHour && contractType !== ContractTypeEnum.Other) && allFilesRequired === true;
    }

    const documentsToFileMetadata = (document?: StoredFileMetadata): [] | FileMetaData[] => {
        if (document) {
            return [{
                name: document.name,
                size: parseInt(document.size),
            }]
        }

        return [];
    }

    const renderPayslipUploadFields = (frequency: string): ReactNode | null => {
        const startDate = storedData.incomeSource.startDate;
        const documents = storedData.documents;
        const payslipKeys = [
            IncomeVerificationIncomeSourceEmploymentDocumentEnum.PayslipOne,
            IncomeVerificationIncomeSourceEmploymentDocumentEnum.PayslipTwo,
            IncomeVerificationIncomeSourceEmploymentDocumentEnum.PayslipThree
        ];
        let periodDiff = 0;

        const start = moment(startDate);
        const now = moment();

        if (frequency === IncomeFrequencyEnum.Monthly) {
            periodDiff = now.diff(start, 'months');
        }

        if (frequency === IncomeFrequencyEnum.Weekly) {
            periodDiff = now.diff(start, 'weeks');
        }

        if (periodDiff === 0) {
            return null;
        }

        const numberOfRequiredPayslips = Math.min(periodDiff, payslipKeys.length);

        return (
            <>
                {payslipKeys.slice(0, numberOfRequiredPayslips).map((key) => (
                    <div key={key}>
                        <Label htmlFor={key} required={allFilesRequired}>
                            {t(`screening.incomeVerification.provideDocuments.${key}.${frequency}`)}
                        </Label>
                        <FileUpload
                            name={key}
                            storedFileMetadata={documentsToFileMetadata(documents?.[key])}
                            acceptedExtensions={['pdf']}
                            errors={formErrors[key]}
                            required={allFilesRequired}
                            onDelete={(name: string) => handleFileUploadDelete(key, name)}
                        />
                    </div>
                ))}
            </>
        );
    };

    return (
        <div>
            <div className={styles.gridMd}>
                <Text
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                >{t('screening.incomeVerification.provideIncomeDetails.employment.title')}</Text>
                <Text>{t('screening.incomeVerification.provideDocuments.explanation')}</Text>
                <div>
                    <Label htmlFor={'employmentContract'} required={allFilesRequired}>
                        {t('screening.incomeVerification.provideDocuments.employmentContract')}
                    </Label>
                    <FileUpload
                        name='employmentContract'
                        storedFileMetadata={documentsToFileMetadata(storedData?.documents['employmentContract'])}
                        acceptedExtensions={['pdf']}
                        errors={formErrors['employmentContract']}
                        required={allFilesRequired}
                        onDelete={(name: string) => handleFileUploadDelete('employmentContract', name)}
                    ></FileUpload>
                </div>
                {contractType !== ContractTypeEnum.Indefinite && (
                    <div>
                        <Label htmlFor={'employerStatement'} required={isEmployerStatementRequired()}>
                            {t('screening.incomeVerification.provideDocuments.employerStatement')}
                        </Label>
                        <FileUpload
                            name='employerStatement'
                            storedFileMetadata={documentsToFileMetadata(storedData?.documents['employerStatement'])}
                            acceptedExtensions={['pdf']}
                            errors={formErrors['employerStatement']}
                            required={isEmployerStatementRequired()}
                            onDelete={(name: string) => handleFileUploadDelete('employerStatement', name)}
                        ></FileUpload>
                    </div>
                )}
                {renderPayslipUploadFields(storedData.incomeSource.incomeFrequency)}
            </div>
            <AnimationWrapperEaseInOut
                showContent={!allRequiredFilesUploaded || !allFilesRequired}
                animatedProperty='height'
                animationDuration={300}
            >
                <div className={styles.checkboxContainer}>
                    <CheckboxInput
                        name='proceedWithoutAllDocuments'
                        id='proceedWithoutAllDocuments'
                        label={t('screening.incomeVerification.provideDocuments.proceedWithoutAllDocuments')}
                        checked={!allFilesRequired}
                        onChange={checked => setAllFilesRequired(!checked)}
                    />
                </div>
            </AnimationWrapperEaseInOut>
        </div>
    )
}

export default EmploymentContent;
